// GutterFrame.tsx — A1, the structural CSS-grid SHELL SKELETON.
// CONTRACT: docs/ux/SHELL-COMPOSITION.md §2.1, §1 (layout regions), §4 (collapse).
//
// THE ONE FIXED SHAPE. Everything else in the named-IA shell docks INTO this
// grid; nothing floats over the map. The frame is `grid-template-rows: 22px 1fr
// auto auto` (banner / stage / census / throughline) with a 3-column STAGE row
// (`var(--rail-w) 1fr var(--ledger-w)`). At REST both gutter columns are width 0
// so the `1fr` MAP column owns ~100% of the glass (map-primacy invariant, §0).
//
// This component is the structural RELEASE VALVE for R-LAW-3 (§2.1, §4.5): it is
// NOT itself a heavy panel and never claims the heavy slot — it is the grid that
// collapse-to-bare and the cockpit/field/workbench layout recipes act on. Width
// transfer is PURE CSS GRID (the `1fr` map column absorbs freed gutter width);
// no element is absolutely positioned over the map, so nothing slides UNDER the
// glass — the map box itself grows (§4.1).
//
// Because a CSS grid GROW can be missed by MapLibre's own ResizeObserver (the
// graybox anti-pattern, §4.2-4.3), GutterFrame attaches its OWN ResizeObserver to
// the map slot and calls `onLayoutSettled()` after each reflow so the orchestrator
// can fire `handle.refitMap()` (re-fit the WebGL2 drawing buffer, camera held).
//
// It reads NO `CopStatus` and calls NO `CopHandle` method directly: the
// orchestrator hands it a `layout` + `bare` prop and the named `slots`, and the
// single `onLayoutSettled` callback. Pure presentation + one observer.

import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import type { LayoutRecipe } from "../host/shellContract.ts";
import "./gutterframe.css";

/** The named render slots GutterFrame docks into its grid regions (§2.1). Each is
 *  an already-composed element handed down by the orchestrator; GutterFrame only
 *  positions them — it never knows what they contain. */
export interface GutterFrameSlots {
  /** grid-row 1, full width: the dual classification BannerStack (z30) — classification
   *  only; identity is NOT in the banner (rail-bottom Profile popover). */
  bannerTop?: ReactNode;
  /** stage col 1 (`--rail-w`): Verdict glyph → Living Spine + Add → contrib anchor. */
  leftGutter?: ReactNode;
  /** stage col 2 (`1fr`): the full-bleed `.cop__map` div (with the host's ref). */
  mapSlot: ReactNode;
  /** stage col 3 (`--ledger-w`): THE LEDGER inspector. */
  rightGutter?: ReactNode;
  /** grid-row 3 (auto): the census "N of M shown" strip. */
  census?: ReactNode;
  /** grid-row 4 (auto): the Throughline omnibox rung. */
  throughline?: ReactNode;
  /** grid-row 5 (auto): ClassificationBanner bottom — a structural row that frames
   *  the map box (no longer an overlay). */
  bannerBottom?: ReactNode;
}

export interface GutterFrameProps {
  /** Device-class layout recipe — drives the gutter widths via `data-layout`. */
  layout: LayoutRecipe;
  /** Collapse-to-bare: zeroes BOTH gutters + hides the census so the map owns
   *  100% of glass. Driven by the one-key collapse (§4). */
  bare: boolean;
  /** The composed region elements. */
  slots: GutterFrameSlots;
  /** Fired AFTER any grid reflow that changed the map slot box, so the
   *  orchestrator can call `handle.refitMap()` on the authoritative settle
   *  (§4.3). Coalesced to one call per animation frame. */
  onLayoutSettled?: () => void;
}

/**
 * The gutter-frame grid skeleton.
 *
 * Mounting (orchestrator, §5 B2): replace the `.cop` body's absolutely-positioned
 * chrome with `<GutterFrame layout={status.layout} bare={…} slots={{ … }}
 * onLayoutSettled={() => handle.refitMap()} />`. The `.cop__map` div (carrying the
 * host's `mapRef`) goes in `slots.mapSlot`.
 */
export function GutterFrame({ layout, bare, slots, onLayoutSettled }: GutterFrameProps): JSX.Element {
  const mapSlotRef = useRef<HTMLDivElement | null>(null);

  // Keep the latest callback in a ref so the ResizeObserver effect (which we want
  // to attach exactly once, for the life of the map slot) always calls the
  // current `onLayoutSettled` without re-subscribing on every render.
  const settledRef = useRef<GutterFrameProps["onLayoutSettled"]>(onLayoutSettled);
  settledRef.current = onLayoutSettled;

  // ResizeObserver on the MAP SLOT (§2.1, §4.3): a grid reflow that grows/shrinks
  // the `1fr` map column fires the observer → we coalesce to one call per frame →
  // `onLayoutSettled()`. The orchestrator's `refitMap()` is idempotent and holds
  // the camera fixed, so calling it on every settle is safe and is the
  // "tracks-don't-slide" guarantee (§4.2). Belt-and-suspenders polled nudges
  // after a layout toggle are the orchestrator's job (§4.3, B3) — here we just
  // report the authoritative settle.
  useEffect(() => {
    const el = mapSlotRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    let rafId = 0;
    const ro = new ResizeObserver(() => {
      // Coalesce a burst of reflow ticks into a single post-frame settle so we
      // read the FINAL box, not an intermediate one mid-transition.
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        settledRef.current?.();
      });
    });
    ro.observe(el);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, []);

  return (
    <div className="gutterframe" data-layout={layout} data-bare={bare ? "" : undefined}>
      <div className="gutterframe__banner-top">{slots.bannerTop}</div>

      <div className="gutterframe__stage">
        <div className="gutterframe__gutter gutterframe__gutter--left">{slots.leftGutter}</div>
        <div className="gutterframe__map-slot" ref={mapSlotRef}>
          {slots.mapSlot}
        </div>
        <div className="gutterframe__gutter gutterframe__gutter--right">{slots.rightGutter}</div>
      </div>

      <div className="gutterframe__census">{slots.census}</div>
      <div className="gutterframe__throughline">{slots.throughline}</div>

      {/* Bottom banner is a STRUCTURAL row (grid-row 5), not an overlay: the map is
          a framed BOX between the top banner stack and this bottom banner — nothing
          plops on top of the glass (the map-primacy "frames it" relationship, §0). */}
      <div className="gutterframe__banner-bottom">{slots.bannerBottom}</div>
    </div>
  );
}
